import React from "react";
import style from "./DebridementViewMode.module.scss";
import { signedLockedAccess } from "../../../../../../utils";
import {
  BLEEDING_AMOUNT,
  DEBRIDEMENT_TYPES,
  DEPTH_OF_DEBRIDEMENT,
  INSTRUMENTS_USED,
  labelFor,
  labelsFor,
  NECROTIC_SUBCUTANEOUS_TISSUE,
  NON_TISSUE_REMOVED,
  NOT_PERFORMED_REASONS,
  TECHNIQUE_USED,
  YES_NO,
} from "../debridementConfig";
import { approachesFor } from "../debridementRules";
import { PERMISSIONS } from "../../../../../../constants/permissions";

const Row = ({ label, value }) =>
  value === null || value === undefined || value === "" || (Array.isArray(value) && !value.length) ? null : (
    <p className={style.view_row}>
      <label>{label}:</label>
      <span>{Array.isArray(value) ? value.join(", ") : value}</span>
    </p>
  );

const ANSWER_LABEL = { yes: "Yes", no: "No", na: "N/A" };

/**
 * Debridement — view mode.
 *
 * Labels are resolved from the bundled config rather than from a fetched
 * option list, so a locked assessment renders correctly offline.
 *
 * Compliance flags are rendered here, not hidden. A record that saved with a
 * gap should say so wherever it is read.
 */

export const DebridementViewMode = ({ resp, prerequisites, flags, noDataMsg, onEdit, assessmentDetails, hasPermission }) => {
  if (noDataMsg || !resp) {
    return (
      <div className={style.cont_spac}>
        <p className={style.nodata_msg}>No data available.</p>
      </div>
    );
  }

  const performed = resp.isDebridementPerformed === "yes";
  const answers = {};
  (resp.prerequisites || []).forEach((p) => {
    answers[p.key] = p;
  });

  return (
    <div className={style.cont_spac}>
      {/* Block 0 */}
      <div className={style.actions}>
        <button
          type="button"
          className={style.tab_edit}
          onClick={onEdit}
          disabled={!hasPermission(PERMISSIONS.TREATMENT_EDIT) || !signedLockedAccess(assessmentDetails?.assessmentStage)}
        >
          <i className="fa fa-pencil" aria-hidden="true"></i>
          Edit
        </button>
      </div>
      <section className={style.block}>
        <div className={style.block_head_static}>
          <span className={style.block_num}>0</span>
          <span className={style.block_title}>Was debridement performed?</span>
        </div>
        <div className={style.block_body}>
          <Row label="Debridement performed" value={performed ? "Yes" : "No"} />
          {!performed && <Row label="Reason not performed" value={labelFor(NOT_PERFORMED_REASONS, resp.notPerformedReason)} />}
        </div>
      </section>

      {/* Block 1 */}
      <section className={style.block}>
        <div className={style.block_head_static}>
          <span className={style.block_num}>1</span>
          <span className={style.block_title}>Prerequisites</span>
        </div>
        <div className={style.block_body}>
          {prerequisites.map((p) => {
            const a = answers[p.key];
            if (!a) return null;
            const selectionLabel = p.options ? labelFor(p.options, a.value) : a.value;
            return (
              <p className={style.view_row} key={p.key}>
                <label>{p.label} :</label>
                <span>
                  {ANSWER_LABEL[a.answer] || "—"}
                  {selectionLabel ? ` · ${selectionLabel}` : ""}
                  {a.answer === "no" && a.reasonText ? ` — ${a.reasonText}` : ""}
                </span>
              </p>
            );
          })}
        </div>
      </section>

      {performed && (
        <>
          {/* Block 2 */}
          <section className={style.block}>
            <div className={style.block_head_static}>
              <span className={style.block_num}>2</span>
              <span className={style.block_title}>Procedure</span>
            </div>
            <div className={style.block_body}>
              <Row label="Type of debridement" value={labelFor(DEBRIDEMENT_TYPES, resp.surgicalDebridementProcedure)} />
              <Row label="Approach" value={labelFor(approachesFor(resp.surgicalDebridementProcedure), resp.nonExcisionalApproach)} />
              <Row label="Deepest tissue removed" value={labelFor(DEPTH_OF_DEBRIDEMENT, resp.depthOfDebridement)} />
              <Row label="Subcutaneous tissue" value={labelsFor(NECROTIC_SUBCUTANEOUS_TISSUE, resp.necroticSubcutaneousTissue)} />
              <Row label="Other non-tissue types removed" value={labelsFor(NON_TISSUE_REMOVED, resp.necroticTissue)} />
              <Row label="Instruments used" value={labelsFor(INSTRUMENTS_USED, resp.instrumentsUsed)} />
              <Row label="Necrotic skin debrided (cm²)" value={resp.surfaceAreaOfNecroticSkinTissue} />
              <Row label="Necrotic subcutaneous tissue debrided (cm²)" value={resp.surfaceAreaOfNecroticSubqTissue} />
              <Row label="Technique used" value={labelFor(TECHNIQUE_USED, resp.techniqueUsed)} />
            </div>
          </section>

          {/* Block 3 */}
          <section className={style.block}>
            <div className={style.block_head_static}>
              <span className={style.block_num}>3</span>
              <span className={style.block_title}>Outcome</span>
            </div>
            <div className={style.block_body}>
              <Row label="Pain right after procedure" value={resp.painAfter === null ? "" : String(resp.painAfter)} />
              <Row label="Bleeding" value={labelFor(BLEEDING_AMOUNT, resp.postDebridementBleedingAmount)} />
              <Row label="Pressure required to stop bleeding" value={labelFor(YES_NO, resp.stoppedBleedingContactPressure)} />
            </div>
          </section>
        </>
      )}

      {flags?.length > 0 && (
        <div className={style.flags} data-testid="view-compliance-flags">
          <div className={style.flags_head}>
            {flags.length} compliance flag{flags.length > 1 ? "s" : ""} on this record
          </div>
          {flags.map((f) => (
            <div className={style.flags_row} key={f.prerequisiteKey}>
              <strong>{f.label}</strong>
              <span>{f.reasonText || f.reasonCode || "No reason given"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
